import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBriteSessionApi,
  getBriteSessionApi,
  CreateBriteSessionRequest,
} from './brite.service';
import { loadBriteClient } from './brite-client-loader';
import {
  BriteSurface,
  BritePendingFlow,
  saveBritePendingFlow,
  clearBritePendingFlow,
} from './brite-resume';
import './brite-widget-host.style.scss';

export const BRITE_WIDGET_CONTAINER_ID = 'brite-widget-host-frame';
const BRITE_WIDGET_CONTAINER_SELECTOR = `#${BRITE_WIDGET_CONTAINER_ID}`;

export interface BriteFlowCallbacks {
  onCompleted?: () => void;
  onClosed?: () => void;
  onError?: (message: string) => void;
}

interface BriteContextValue {
  isCreatingSession: boolean;
  isWidgetActive: boolean;
  startBriteFlow: (payload: CreateBriteSessionRequest, surface: BriteSurface, callbacks?: BriteFlowCallbacks) => Promise<void>;
  resumeBriteFlow: (flow: BritePendingFlow, callbacks?: BriteFlowCallbacks) => Promise<void>;
  finalizeBriteFlow: (flow: BritePendingFlow, callbacks?: BriteFlowCallbacks) => Promise<void>;
  stopBriteFlow: () => void;
}

const BriteContext = createContext<BriteContextValue | null>(null);

export const useBriteContext = (): BriteContextValue => {
  const ctx = useContext(BriteContext);
  if (!ctx) {
    throw new Error('useBriteContext must be used within <BriteWidgetHost>');
  }
  return ctx;
};

export const BriteWidgetHost: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isWidgetActive, setIsWidgetActive] = useState(false);
  const clientRef = useRef<any>(null);
  const cancelledRef = useRef(false);
  const callbacksRef = useRef<BriteFlowCallbacks>({});

  const attachWidget = useCallback((token: string, sessionId: string) => {
    const BriteCtor = window.Brite;
    if (!BriteCtor) {
      throw new Error('Brite client failed to load');
    }

    const client = new BriteCtor(token);
    clientRef.current = client;
    setIsWidgetActive(true);
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 480;

    client.start(
      {
        selector: BRITE_WIDGET_CONTAINER_SELECTOR,
        width: isMobileViewport ? '90vw' : '440px',
        height: isMobileViewport ? '90vh' : '600px',
        color_theme: 'light',
      },
      async (state: any) => {
        if (state === BriteCtor.STATE_COMPLETED) {
          try {
            await getBriteSessionApi(sessionId);
          } finally {
            client.stop();
            clientRef.current = null;
            setIsWidgetActive(false);
            clearBritePendingFlow();
            callbacksRef.current.onCompleted?.();
          }
        } else if (state === BriteCtor.STATE_FAILED || state === BriteCtor.STATE_ABORTED) {
          clearBritePendingFlow();
        }
      },
      () => {
        client.stop();
        setIsWidgetActive(false);
        callbacksRef.current.onClosed?.();
      }
    );
  }, []);

  const startBriteFlow = useCallback(
    async (payload: CreateBriteSessionRequest, surface: BriteSurface, callbacks: BriteFlowCallbacks = {}) => {
      callbacksRef.current = callbacks;
      cancelledRef.current = false;
      setIsCreatingSession(true);
      try {
        const session = await createBriteSessionApi(payload);
        const sessionToken = session.response?.token;
        const sessionId = session.response?.id;
        await loadBriteClient();

        if (cancelledRef.current) return;

        if (sessionToken && sessionId) {
          saveBritePendingFlow({
            token: sessionToken,
            sessionId,
            surface,
            createdAt: Date.now(),
          });
        }

        attachWidget(sessionToken, sessionId);
      } catch (err: any) {
        setIsWidgetActive(false);
        callbacksRef.current.onError?.(err?.response?.data?.message || err?.message || 'Failed to start Brite');
      } finally {
        setIsCreatingSession(false);
      }
    },
    [attachWidget]
  );

  const resumeBriteFlow = useCallback(
    async (flow: BritePendingFlow, callbacks: BriteFlowCallbacks = {}) => {
      callbacksRef.current = callbacks;
      cancelledRef.current = false;
      setIsCreatingSession(true);
      try {
        await loadBriteClient();
        if (cancelledRef.current) return;
        attachWidget(flow.token, flow.sessionId);
      } catch (err: any) {
        setIsWidgetActive(false);
        callbacksRef.current.onError?.(err?.response?.data?.message || err?.message || 'Failed to resume Brite');
      } finally {
        setIsCreatingSession(false);
      }
    },
    [attachWidget]
  );

  const finalizeBriteFlow = useCallback(
    async (flow: BritePendingFlow, callbacks: BriteFlowCallbacks = {}) => {
      callbacksRef.current = callbacks;
      try {
        await getBriteSessionApi(flow.sessionId);
        callbacksRef.current.onCompleted?.();
      } catch (err: any) {
        callbacksRef.current.onError?.(err?.response?.data?.message || err?.message || 'Failed to finalize Brite session');
      } finally {
        clearBritePendingFlow();
      }
    },
    []
  );

  const stopBriteFlow = useCallback(() => {
    cancelledRef.current = true;
    clientRef.current?.stop?.();
    clientRef.current = null;
    setIsWidgetActive(false);
    setIsCreatingSession(false);
  }, []);

  const value: BriteContextValue = {
    isCreatingSession,
    isWidgetActive,
    startBriteFlow,
    resumeBriteFlow,
    finalizeBriteFlow,
    stopBriteFlow,
  };

  return (
    <BriteContext.Provider value={value}>
      {children}
      <div className={`brite-widget-overlay ${(isCreatingSession || isWidgetActive) ? 'is-active' : ''}`}>
        <div className="brite-widget-overlay__backdrop" />
        <div className="brite-widget-overlay__dialog" role="dialog" aria-modal="true">
          <div className="brite-widget-overlay__header">
            <span className="brite-widget-overlay__title">{t('CONNECT_WITH_BRITE') || 'Retrieve your bank details automatically'}</span>
            <button
              type="button"
              className="brite-widget-overlay__close"
              aria-label="Close"
              onClick={stopBriteFlow}
            >
              &times;
            </button>
          </div>
          <div className="brite-widget-overlay__body">
            {isCreatingSession && !isWidgetActive && (
              <div className="brite-widget-overlay__loading">
                <span className="brite-widget-overlay__spinner" aria-hidden="true" />
                {t('BRITE_CONNECTING') || 'Connecting…'}
              </div>
            )}
            <div id={BRITE_WIDGET_CONTAINER_ID} className="brite-widget-overlay__frame" />
          </div>
        </div>
      </div>
    </BriteContext.Provider>
  );
};
